import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  Calendar,
  Plus,
  Lock,
  Users,
  Settings,
  Check,
  X
} from 'lucide-react';
import Header from '../components/Layout/Header';
import { doctors, appointments } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 100px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const DoctorInfo = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const DoctorImage = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DoctorDetails = styled.div`
  flex: 1;
`;

const DoctorName = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const DoctorSpecialty = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const QuickActionButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  svg {
    width: 28px;
    height: 28px;
    color: ${({ theme }) => theme.colors.primary};
  }

  span {
    font-size: 13px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.text};
    text-align: center;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const CalendarCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const MonthYear = styled.h2`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 8px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const DayCell = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${({ $isSelected, $isToday, $hasAppointments, theme }) => 
    $isSelected ? theme.colors.primary : 
    $isToday ? theme.colors.primaryLight :
    'transparent'};
  color: ${({ $isSelected, $isOtherMonth, theme }) => 
    $isSelected ? theme.colors.white :
    $isOtherMonth ? '#D0D0D0' :
    theme.colors.text};
  border-radius: 50%;
  font-size: 14px;
  font-weight: ${({ $isSelected, $isToday, theme }) => 
    $isSelected || $isToday ? theme.fontWeights.semibold : theme.fontWeights.normal};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover {
    background: ${({ $isSelected, theme }) => 
      $isSelected ? theme.colors.primaryDark : theme.colors.gray};
  }
`;

const AppointmentDot = styled.div`
  position: absolute;
  bottom: 4px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ $isSelected, theme }) => 
    $isSelected ? theme.colors.white : theme.colors.primary};
`;

const SectionCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const ScheduleGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ScheduleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: ${({ $active, theme }) => $active ? theme.colors.primaryLight : theme.colors.gray};
  border-radius: 10px;
`;

const DayName = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  min-width: 80px;
`;

const TimeRange = styled.span`
  font-size: 14px;
  color: ${({ $active, theme }) => $active ? theme.colors.text : theme.colors.textSecondary};
`;

const StatusIndicator = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active, theme }) => $active ? theme.colors.success : theme.colors.border};

  svg {
    width: 14px;
    height: 14px;
    color: ${({ $active, theme }) => $active ? theme.colors.white : theme.colors.textSecondary};
  }
`;

const AppointmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AppointmentCard = styled.div`
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid ${({ $status, theme }) => 
    $status === 'confirmada' ? theme.colors.success :
    $status === 'pendiente' ? theme.colors.warning :
    theme.colors.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`;

const AppointmentTime = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  padding: 8px 12px;
  text-align: center;
  min-width: 60px;
`;

const TimeText = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const AppointmentInfo = styled.div`
  flex: 1;
`;

const PatientName = styled.h4`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const AppointmentType = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const AppointmentStatus = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  padding: 4px 10px;
  border-radius: 20px;
  background: ${({ $status, theme }) => 
    $status === 'confirmada' ? theme.colors.success :
    $status === 'pendiente' ? theme.colors.warning :
    theme.colors.primaryLight};
  color: ${({ $status, theme }) => 
    $status === 'confirmada' ? theme.colors.white :
    $status === 'pendiente' ? '#856404' :
    theme.colors.primary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;

  svg {
    width: 48px;
    height: 48px;
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ $color, theme }) => $color || theme.colors.primary};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const GestionarAgenda = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const doctor = doctors.find(d => d.id === parseInt(id)) || doctors[0];
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Horario de trabajo del médico
  const workSchedule = [
    { day: 'Lunes', active: true, start: '09:00', end: '18:00' },
    { day: 'Martes', active: true, start: '09:00', end: '18:00' },
    { day: 'Miércoles', active: true, start: '09:00', end: '14:00' },
    { day: 'Jueves', active: true, start: '09:00', end: '18:00' },
    { day: 'Viernes', active: true, start: '09:00', end: '16:00' },
    { day: 'Sábado', active: false, start: '', end: '' },
    { day: 'Domingo', active: false, start: '', end: '' },
  ];

  // Citas del día seleccionado (mock)
  const dayAppointments = [
    { id: 1, time: '09:00', patient: 'María García', type: 'Consulta General', status: 'confirmada' },
    { id: 2, time: '10:30', patient: 'Juan Pérez', type: 'Seguimiento', status: 'pendiente' },
    { id: 3, time: '12:00', patient: 'Ana López', type: 'Primera Consulta', status: 'confirmada' },
  ];

  // Estadísticas del día
  const stats = {
    total: dayAppointments.length,
    confirmadas: dayAppointments.filter(a => a.status === 'confirmada').length,
    pendientes: dayAppointments.filter(a => a.status === 'pendiente').length,
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isOtherMonth: true,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isOtherMonth: false,
        date: new Date(year, month, i)
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isOtherMonth: true,
        date: new Date(year, month + 1, i)
      });
    }

    return days;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    return date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
  };

  const hasAppointments = (dayInfo) => {
    // Simular que algunos días tienen citas
    return [10, 12, 15, 18, 22].includes(dayInfo.date.getDate()) && !dayInfo.isOtherMonth;
  };

  const formatSelectedDate = () => {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return selectedDate.toLocaleDateString('es-ES', options);
  };

  return (
    <PageContainer>
      <Header title="Gestionar Agenda" showBack />
      
      <Content>
        {/* Info del médico */}
        <DoctorInfo>
          <DoctorImage>
            <img src={doctor.image} alt={doctor.name} />
          </DoctorImage>
          <DoctorDetails>
            <DoctorName>{doctor.name}</DoctorName>
            <DoctorSpecialty>{doctor.specialty}</DoctorSpecialty>
          </DoctorDetails>
        </DoctorInfo>

        {/* Acciones rápidas */}
        <QuickActions>
          <QuickActionButton onClick={() => navigate('/reservar-cita')}>
            <Plus />
            <span>Nueva Cita</span>
          </QuickActionButton>
          <QuickActionButton onClick={() => navigate(`/bloquear-horarios/${doctor.id}`)}>
            <Lock />
            <span>Bloquear Horarios</span>
          </QuickActionButton>
          <QuickActionButton onClick={() => navigate(`/gestion-servicios/${doctor.id}`)}>
            <Settings />
            <span>Servicios</span>
          </QuickActionButton>
          <QuickActionButton onClick={() => navigate(`/perfil-medico/${doctor.id}`)}>
            <Users />
            <span>Ver Perfil</span>
          </QuickActionButton>
        </QuickActions>

        {/* Calendario */}
        <CalendarCard>
          <CalendarHeader>
            <MonthYear>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</MonthYear>
            <NavButtons>
              <NavButton onClick={() => navigateMonth(-1)}>
                <ChevronLeft />
              </NavButton>
              <NavButton onClick={() => navigateMonth(1)}>
                <ChevronRight />
              </NavButton>
            </NavButtons>
          </CalendarHeader>

          <WeekDays>
            {weekDays.map(day => (
              <WeekDay key={day}>{day}</WeekDay>
            ))}
          </WeekDays>

          <DaysGrid>
            {getDaysInMonth(currentDate).map((dayInfo, index) => (
              <DayCell
                key={index}
                $isOtherMonth={dayInfo.isOtherMonth}
                $isSelected={isSelected(dayInfo.date)}
                $isToday={isToday(dayInfo.date)}
                $hasAppointments={hasAppointments(dayInfo)}
                onClick={() => setSelectedDate(dayInfo.date)}
              >
                {dayInfo.day}
                {hasAppointments(dayInfo) && (
                  <AppointmentDot $isSelected={isSelected(dayInfo.date)} />
                )}
              </DayCell>
            ))}
          </DaysGrid>
        </CalendarCard>

        {/* Estadísticas del día */}
        <StatsRow>
          <StatCard>
            <StatNumber>{stats.total}</StatNumber>
            <StatLabel>Total Citas</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber $color="#28A745">{stats.confirmadas}</StatNumber>
            <StatLabel>Confirmadas</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber $color="#FFC107">{stats.pendientes}</StatNumber>
            <StatLabel>Pendientes</StatLabel>
          </StatCard>
        </StatsRow>

        {/* Citas del día seleccionado */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>
              <Calendar />
              Citas - {formatSelectedDate()}
            </SectionTitle>
          </SectionHeader>

          {dayAppointments.length > 0 ? (
            <AppointmentsList>
              {dayAppointments.map((appointment) => (
                <AppointmentCard 
                  key={appointment.id}
                  $status={appointment.status}
                  onClick={() => navigate(`/detalle-cita/${appointment.id}`)}
                >
                  <AppointmentTime>
                    <TimeText>{appointment.time}</TimeText>
                  </AppointmentTime>
                  <AppointmentInfo>
                    <PatientName>{appointment.patient}</PatientName>
                    <AppointmentType>{appointment.type}</AppointmentType>
                  </AppointmentInfo>
                  <AppointmentStatus $status={appointment.status}>
                    {appointment.status}
                  </AppointmentStatus>
                </AppointmentCard>
              ))}
            </AppointmentsList>
          ) : (
            <EmptyState>
              <Calendar />
              <p>No hay citas para este día</p>
            </EmptyState>
          )}
        </SectionCard>

        {/* Horario de trabajo */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>
              <Clock />
              Horario de Trabajo
            </SectionTitle>
            <EditButton>
              <Settings size={14} />
              Editar
            </EditButton>
          </SectionHeader>

          <ScheduleGrid>
            {workSchedule.map((schedule, index) => (
              <ScheduleRow key={index} $active={schedule.active}>
                <DayName>{schedule.day}</DayName>
                <TimeRange $active={schedule.active}>
                  {schedule.active ? `${schedule.start} - ${schedule.end}` : 'No disponible'}
                </TimeRange>
                <StatusIndicator $active={schedule.active}>
                  {schedule.active ? <Check /> : <X />}
                </StatusIndicator>
              </ScheduleRow>
            ))}
          </ScheduleGrid>
        </SectionCard>
      </Content>
    </PageContainer>
  );
};

export default GestionarAgenda;
